import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Alert,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AppDialog from '../common/AppDialog';
import {
  CameraAlt as CameraIcon,
  Keyboard as KeyboardIcon,
  QrCodeScanner as QrCodeScannerIcon,
  Stop as StopIcon,
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import BarcodeCameraReader from './BarcodeCameraReader';

interface BarcodeScannerDialogProps {
  open: boolean;
  onClose: () => void;
  onDetected: (barcode: string) => void;
  title?: string;
}

/** Camera, keyboard-wedge, and manual barcode input in a reusable dialog. */
export function BarcodeScannerDialog({ open, onClose, onDetected, title }: BarcodeScannerDialogProps) {
  const { t } = useLanguage();
  const inputRef = useRef<HTMLInputElement>(null);
  const [barcode, setBarcode] = useState('');
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState(false);

  useEffect(() => {
    if (!open) {
      setCameraActive(false);
      return undefined;
    }

    setBarcode('');
    setCameraError(false);
    // Focus after the portal and transition mount for keyboard-wedge scanners,
    // but avoid opening the software keyboard on touch-first devices.
    if (window.matchMedia?.('(pointer: coarse)')?.matches) return undefined;
    const focusTimer = window.setTimeout(() => inputRef.current?.focus(), 100);
    return () => window.clearTimeout(focusTimer);
  }, [open]);

  const submitBarcode = () => {
    const normalizedBarcode = barcode.trim();
    if (!normalizedBarcode) return;

    setCameraActive(false);
    onDetected(normalizedBarcode);
    setBarcode('');
  };

  const handleClose = () => {
    setCameraActive(false);
    onClose();
  };

  const handleCameraDetected = (value: string) => {
    setCameraActive(false);
    onDetected(value.trim());
  };

  const handleCameraError = () => {
    setCameraActive(false);
    setCameraError(true);
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    submitBarcode();
  };

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return;
    // Some keyboard-wedge scanners send Enter as a key event without
    // triggering the form's native submit event in every browser.
    event.preventDefault();
    submitBarcode();
  };

  return (
    <AppDialog presentation="compact" open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title || t('barcode.scanTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {t('barcode.scannerHint')}
          </Typography>
          {cameraError && (
            <Alert severity="warning">{t('barcode.cameraUnavailable')}</Alert>
          )}
          {cameraActive ? (
            <Stack spacing={1}>
              <Box sx={{ width: '100%', overflow: 'hidden', borderRadius: 1 }}>
                <BarcodeCameraReader onDetected={handleCameraDetected} onError={handleCameraError} />
              </Box>
              <Button
                variant="outlined"
                startIcon={<StopIcon />}
                onClick={() => setCameraActive(false)}
              >
                {t('barcode.stopCamera')}
              </Button>
            </Stack>
          ) : (
            <Button
              variant="outlined"
              startIcon={<CameraIcon />}
              onClick={() => {
                setCameraError(false);
                setCameraActive(true);
              }}
            >
              {t('barcode.startCamera')}
            </Button>
          )}
          <Box component="form" onSubmit={handleSubmit}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <TextField
                inputRef={inputRef}
                fullWidth
                size="small"
                label={t('barcode.scannerInput')}
                value={barcode}
                onChange={(event) => setBarcode(event.target.value)}
                onKeyDown={handleInputKeyDown}
                inputProps={{
                  'data-testid': 'barcode-manual-input',
                  inputMode: 'text',
                  autoComplete: 'off',
                }}
              />
              <Button
                type="submit"
                variant="outlined"
                startIcon={<KeyboardIcon />}
                disabled={!barcode.trim()}
              >
                {t('barcode.useCode')}
              </Button>
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t('common.cancel')}</Button>
      </DialogActions>
    </AppDialog>
  );
}

interface BarcodeScanButtonProps {
  onDetected: (barcode: string) => void;
  label: string;
  size?: 'small' | 'medium' | 'large';
}

export function BarcodeScanButton({ onDetected, label, size = 'small' }: BarcodeScanButtonProps) {
  const [open, setOpen] = useState(false);

  const handleDetected = (barcode: string) => {
    setOpen(false);
    onDetected(barcode);
  };

  return (
    <>
      <Button
        variant="outlined"
        size={size}
        startIcon={<QrCodeScannerIcon />}
        onClick={() => setOpen(true)}
        data-testid="barcode-scan-button"
      >
        {label}
      </Button>
      <BarcodeScannerDialog
        open={open}
        onClose={() => setOpen(false)}
        onDetected={handleDetected}
      />
    </>
  );
}
